import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { VITALS, VITALS_BY_KEY, pctLabel } from "./vitalCategories";
import {
  ET_CATEGORIES,
  ET_HIGHLIGHTS,
  resolveHighlightKey,
  resolveThresholdKey,
} from "./reportViews";
import dmSansRegular from "./fonts/dmSansRegular";
import dmSansBold from "./fonts/dmSansBold";
import tocaLogoPng from "./tocaLogo";
import { buildReportFilename, DOCTYPES } from "../../../utils/fileNaming";

/**
 * OOR Report PDF + Excel export (SCRUM-192).
 *
 * The PDFs are drawn as VECTOR jsPDF with the real DM Sans font embedded, so
 * the typeface and text widths match the Figma exactly AND every colored dot
 * is placed at the precise vertical center of its text (the html2canvas
 * approach baseline-aligned the dots, dropping them low). Category =
 * Figma 1859-1283, List = Figma 1860-1925.
 *
 * TODO(SCRUM-192/backend): log the download (user + timestamp) to report
 * history once the events endpoint exists.
 */

const NAVY = [13, 17, 57];
const LIGHT = [86, 89, 117];
const LINE = [201, 203, 213];
const TRACK = [241, 245, 249];

let _fontsReady = false;
const registerFonts = (doc) => {
  doc.addFileToVFS("DMSans-Regular.ttf", dmSansRegular);
  doc.addFont("DMSans-Regular.ttf", "DMSans", "normal");
  doc.addFileToVFS("DMSans-Bold.ttf", dmSansBold);
  doc.addFont("DMSans-Bold.ttf", "DMSans", "bold");
  _fontsReady = true;
};

// DM Sans latin subset has no >= / <= glyphs; substitute ASCII for the ranges.
const clean = (t) =>
  String(t == null ? "" : t).replace(/≥/g, ">=").replace(/≤/g, "<=");

const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return [156, 163, 175];
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const datePart = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  let hours = d.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${datePart} ${hours}:${minutes}${ampm}`;
};

// periodDate is the selected report range's end date (YYYY-MM-DD), when known.
const yearMonthFromPeriodDate = (periodDate) => {
  if (!periodDate) return {};
  const [year, month] = periodDate.split("-");
  return { year, month };
};

const MARGIN = 10;
const PAGE_W = 210;
const CONTENT_W = PAGE_W - MARGIN * 2;

const dot = (doc, cx, cy, color, r = 1.3) => {
  const [rr, gg, bb] = hexToRgb(color);
  doc.setFillColor(rr, gg, bb);
  doc.circle(cx, cy, r, "F");
};

/* ── header ───────────────────────────────────────────────────────── */

const drawHeader = (doc, { accountName, dateRange, providerLabel, reportTypeLabel }) => {
  const rightLabelX = PAGE_W / 2 + 28;
  const pair = (labelX, valX, y, label, value) => {
    doc.setFont("DMSans", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...LIGHT);
    doc.text(label, labelX, y, { baseline: "middle" });
    doc.setFont("DMSans", "bold");
    doc.setTextColor(...NAVY);
    doc.text(String(value || "—"), valX, y, { baseline: "middle" });
  };
  pair(MARGIN, MARGIN + 28, 15, "Account Name :", accountName);
  pair(rightLabelX, rightLabelX + 26, 15, "Report Type :", reportTypeLabel);
  pair(MARGIN, MARGIN + 28, 21, "Date Range :", dateRange);
  pair(rightLabelX, rightLabelX + 26, 21, "Provider :", providerLabel);
  return 28;
};

/* ── legend (Figma 2-row cards; dots centered on the label line) ────── */

const drawLegend = (doc, vitalKeys, startY) => {
  const present = VITALS.filter((v) => vitalKeys.includes(v.key));
  if (!present.length) return startY;

  const PAD = 3;
  const DOT_R = 1.55;
  const DOT_GAP = 2;
  const COL_GAP = 5; // default breathing room after each category column
  const MIN_COL_GAP = 2; // floor when a wide card has to tighten to fit one line
  const TITLE_PT = 8.5;
  const LABEL_PT = 8;
  const RANGE_PT = 6.5;
  const LABEL_DY = 6.5; 
  const RANGE_DY = 10.5; 
  const CARD_H = 15.5;
  const CARD_GAP = 6;
  const ROW_GAP = 4;
  const maxX = PAGE_W - MARGIN;
  const maxCardW = maxX - MARGIN - 1;
  const colsFor = (v, colGap) =>
    v.categories.map((c) => {
      doc.setFont("DMSans", "normal");
      doc.setFontSize(LABEL_PT);
      const lw = doc.getTextWidth(c.label);
      doc.setFontSize(RANGE_PT);
      const rw = doc.getTextWidth(clean(c.range));
      return { c, w: DOT_R * 2 + DOT_GAP + Math.max(lw, rw) + colGap };
    });

  const cardWidth = (v, cols) => {
    doc.setFont("DMSans", "bold");
    doc.setFontSize(TITLE_PT);
    const catsW = cols.reduce((s, c) => s + c.w, 0);
    return Math.max(doc.getTextWidth(v.label) + 6, catsW + PAD * 2);
  };

  let x = MARGIN;
  let y = startY + 2;

  present.forEach((v) => {
   
    let cols = colsFor(v, COL_GAP);
    let cardW = cardWidth(v, cols);
    if (cardW > maxCardW) {
      const overflowPerCol = (cardW - maxCardW) / v.categories.length;
      const colGap = Math.max(MIN_COL_GAP, COL_GAP - overflowPerCol);
      cols = colsFor(v, colGap);
      cardW = cardWidth(v, cols);
    }

    if (x !== MARGIN && x + cardW > maxX) {
      x = MARGIN;
      y += CARD_H + ROW_GAP;
    }

    // card border
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, y, cardW, CARD_H, 1.5, 1.5, "S");

    // title
    doc.setFont("DMSans", "bold");
    doc.setFontSize(TITLE_PT);
    doc.setTextColor(...NAVY);
    doc.text(v.label, x + PAD, y + 3.2, { baseline: "middle" });

    // categories: dot centered on the label line, range below. The dot is
    // nudged down to the label's optical center (jsPDF's "middle" text
    // baseline sits a touch above the dot center for DM Sans).
    let cx = x + PAD;
    cols.forEach(({ c, w }) => {
      dot(doc, cx + DOT_R, y + LABEL_DY + 0.3, c.color, DOT_R);
      doc.setFont("DMSans", "normal");
      doc.setTextColor(...LIGHT);
      doc.setFontSize(LABEL_PT);
      doc.text(c.label, cx + DOT_R * 2 + DOT_GAP, y + LABEL_DY, {
        baseline: "middle",
      });
      doc.setFontSize(RANGE_PT);
      doc.text(clean(c.range), cx + DOT_R * 2 + DOT_GAP, y + RANGE_DY, {
        baseline: "middle",
      });
      cx += w;
    });

    x += cardW + CARD_GAP;
  });

  return y + CARD_H + 5;
};

const drawFooter = (doc, pageNumber, totalPages) => {
  const y = 297 - 8;
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y - 3, PAGE_W - MARGIN, y - 3);
  doc.setFont("DMSans", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...LIGHT);
  doc.text("Out of Range Report", MARGIN, y, { baseline: "middle" });
  const pageText = `Page ${pageNumber} of ${totalPages}`;
  doc.text(pageText, (PAGE_W - doc.getTextWidth(pageText)) / 2, y, {
    baseline: "middle",
  });
  const stamp = new Date().toLocaleString();
  doc.text(stamp, PAGE_W - MARGIN - doc.getTextWidth(stamp), y, {
    baseline: "middle",
  });
};

const tableTheme = (startY, columnStyles) => ({
  startY,
  margin: { left: MARGIN, right: MARGIN, bottom: 16 },
  rowPageBreak: "avoid",
  styles: {
    font: "DMSans",
    fontStyle: "normal",
    fontSize: 8,
    cellPadding: 2,
    textColor: NAVY,
    valign: "top",
    lineColor: LINE,
    lineWidth: 0.2,
  },
  headStyles: {
    font: "DMSans",
    fontStyle: "bold",
    fillColor: NAVY,
    textColor: [255, 255, 255],
    fontSize: 8,
    valign: "middle",
    halign: "left",
    cellPadding: { top: 4, bottom: 4, left: 2, right: 2 },
  },
  columnStyles,
});

const drawName = (doc, x, y, row, maxW) => {
  doc.setFont("DMSans", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...NAVY);
  const lines = doc.splitTextToSize(row.patientName || "—", maxW || 40);
  let ny = y + 3.5;
  lines.forEach((ln) => {
    doc.text(ln, x, ny, { baseline: "middle" });
    ny += 4;
  });
  if (row.dob) {
    doc.setFont("DMSans", "normal");
    doc.setTextColor(...LIGHT);
    doc.text(row.dob, x, ny + 0.5, { baseline: "middle" });
  }
};

/* ── Category Report PDF (Figma 1859-1283) ─────────────────────────── */

const CAT_BLOCK = 15;

const BAR_FONT_SIZE = 5.5;
const BAR_LABEL_PAD = 0.7; // mm of breathing room either side of the label


const layoutBarSegments = (doc, w, distribution) => {
  const labels = distribution.map((d) => pctLabel(d.pct));
  const mins = labels.map((t) => doc.getTextWidth(t) + BAR_LABEL_PAD * 2);
  const natural = distribution.map((d) => (d.pct / 100) * w);
  const widths = natural.slice();
  const pinned = natural.map(() => false);

  for (let pass = 0; pass < distribution.length; pass += 1) {
    let pinnedAny = false;
    widths.forEach((cw, i) => {
      if (!pinned[i] && cw < mins[i]) {
        pinned[i] = true;
        widths[i] = mins[i];
        pinnedAny = true;
      }
    });
    if (!pinnedAny) break;
    const fixed = widths.reduce((s, cw, i) => (pinned[i] ? s + cw : s), 0);
    const flexShare = natural.reduce((s, nw, i) => (pinned[i] ? s : s + nw), 0);
    if (!flexShare) break;
    const avail = Math.max(w - fixed, 0);
    natural.forEach((nw, i) => {
      if (!pinned[i]) widths[i] = (nw / flexShare) * avail;
    });
  }

  
  const total = widths.reduce((s, cw) => s + cw, 0);
  if (total > w) {
    const k = w / total;
    widths.forEach((cw, i) => {
      widths[i] = cw * k;
    });
  }
  return widths.map((cw, i) => ({
    width: cw,
    label: cw >= mins[i] - BAR_LABEL_PAD ? labels[i] : null,
  }));
};

const drawBar = (doc, x, y, w, vital) => {
  const h = 3.4;
  doc.setFillColor(...TRACK);
  doc.roundedRect(x, y, w, h, 0.8, 0.8, "F");
  doc.setFont("DMSans", "normal");
  doc.setFontSize(BAR_FONT_SIZE);
  const segments = layoutBarSegments(doc, w, vital.distribution);
  let cx = x;
  vital.distribution.forEach((d, i) => {
    const { width: segW, label } = segments[i];
    if (segW <= 0) return;
    const [r, g, b] = hexToRgb(d.color);
    doc.setFillColor(r, g, b);
    doc.rect(cx, y, segW, h, "F");
    if (label) {
      doc.setTextColor(255, 255, 255);
      doc.text(label, cx + segW / 2, y + h / 2, {
        align: "center",
        baseline: "middle",
      });
    }
    cx += segW;
  });
};

export const generateCategoryReportPDF = ({
  accountName,
  dateRange,
  periodDate,
  providerLabel,
  vitalKeys = [],
  rows = [],
  // asBlob: return { blob, filename } (for the share upload) instead of
  // triggering the browser download. Same document either way.
  asBlob = false,
}) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  registerFonts(doc);
  let startY = drawHeader(doc, {
    accountName,
    dateRange,
    providerLabel,
    reportTypeLabel: "Category Report",
  });
  startY = drawLegend(doc, vitalKeys, startY);

  const rowHeights = rows.map((r) => (r.vitals.length || 1) * CAT_BLOCK + 3);

  autoTable(doc, {
    head: [["Patient Name", "Provider", "No. of Readings", "Category"]],
    body: rows.map((r) => ["", r.providerName || "—", "", ""]),
    ...tableTheme(startY, {
      0: { cellWidth: 30 },
      1: { cellWidth: 25 },
      2: { cellWidth: 55 },
      3: { cellWidth: 80 },
    }),
    alternateRowStyles: { fillColor: [248, 250, 253] },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 0) {
        data.cell.styles.minCellHeight = rowHeights[data.row.index];
      }
    },
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      const row = rows[data.row.index];
      if (!row) return;
      const { x, y, width } = data.cell;
      const pad = 2;
      if (data.column.index === 0) {
        drawName(doc, x + pad, y + pad, row, width - pad * 2);
      } else if (data.column.index === 2) {
        let by = y + pad;
        row.vitals.forEach((v) => {
          doc.setFont("DMSans", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...NAVY);
          doc.text(v.label, x + pad, by + 3, { baseline: "middle" });
          doc.setFont("DMSans", "normal");
          doc.text(`${v.total} Total readings`, x + pad, by + 7.5, { baseline: "middle" });
          doc.text(`${v.outOfRange} Out of range`, x + pad, by + 11.5, { baseline: "middle" });
          by += CAT_BLOCK;
        });
      } else if (data.column.index === 3) {
        let by = y + pad;
        row.vitals.forEach((v) => {
          doc.setFont("DMSans", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...NAVY);
          doc.text(v.label, x + pad, by + 3, { baseline: "middle" });
          drawBar(doc, x + pad, by + 6, width - pad * 2, v);
          by += CAT_BLOCK;
        });
      }
    },
    didDrawPage: (data) => drawFooter(doc, data.pageNumber, doc.internal.pages.length - 1),
  });

  const categoryFilename = buildReportFilename({
    ...yearMonthFromPeriodDate(periodDate),
    doctype: DOCTYPES.OOR_CATEGORY,
    program: "RPM",
    ext: "pdf",
  });
  if (asBlob) return { blob: doc.output("blob"), filename: categoryFilename };
  doc.save(categoryFilename);
};

/* ── List Report PDF (Figma 1860-1925) ─────────────────────────────── */

const LIST_LABEL = 5;
const LIST_LINE = 4.7;

export const generateListReportPDF = ({
  accountName,
  dateRange,
  periodDate,
  providerLabel,
  vitalKeys = [],
  listRows = [],
  asBlob = false,
}) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  registerFonts(doc);
  let startY = drawHeader(doc, {
    accountName,
    dateRange,
    providerLabel,
    reportTypeLabel: "List Report",
  });
  startY = drawLegend(doc, vitalKeys, startY);

  const rowHeights = listRows.map((r) => {
    let h = 3;
    r.vitals.forEach((v) => {
      h += LIST_LABEL + v.readings.length * LIST_LINE;
    });
    return h + 3;
  });

  autoTable(doc, {
    head: [["Patient Name", "Provider", "No. of Readings", "Category"]],
    body: listRows.map((r) => ["", r.providerName || "—", "", ""]),
    ...tableTheme(startY, {
      0: { cellWidth: 26 },
      1: { cellWidth: 16 },
      2: { cellWidth: 74 },
      3: { cellWidth: 74 },
    }),
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 0) {
        data.cell.styles.minCellHeight = rowHeights[data.row.index];
      }
    },
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      const row = listRows[data.row.index];
      if (!row) return;
      const { x, y, width } = data.cell;
      const pad = 2;
      if (data.column.index === 0) {
        drawName(doc, x + pad, y + pad, row, width - pad * 2);
      } else if (data.column.index === 2) {
        let by = y + pad + 3;
        row.vitals.forEach((v) => {
          doc.setFont("DMSans", "bold");
          doc.setFontSize(8);
          doc.setTextColor(...NAVY);
          doc.text(v.label, x + pad, by, { baseline: "middle" });
          by += LIST_LABEL;
          v.readings.forEach((it) => {
            const cy = by - 1; // line center
            dot(doc, x + pad + 1.1, cy, it.color, 1.1);
            doc.setFont("DMSans", "normal");
            doc.setFontSize(7);
            doc.setTextColor(...NAVY);
            doc.text(String(it.value), x + pad + 4, cy, { baseline: "middle" });
            if (it.secondary) {
              dot(doc, x + pad + 19, cy, it.secondary.color, 1.1);
              doc.text(String(it.secondary.value), x + pad + 22, cy, { baseline: "middle" });
            }
            doc.setFontSize(6.5);
            doc.setTextColor(...LIGHT);
            doc.text(it.dateText || "", x + pad + 33, cy, { baseline: "middle" });
            by += LIST_LINE;
          });
        });
      } else if (data.column.index === 3) {
        let by = y + pad + 3 + LIST_LABEL;
        row.vitals.forEach((v) => {
          v.readings.forEach((it) => {
            const cy = by - 1;
            let cxx = x + pad;
            doc.setFont("DMSans", "normal");
            doc.setFontSize(7);
            (it.categories || []).forEach((c) => {
              dot(doc, cxx + 1.1, cy, c.color, 1.1);
              doc.setTextColor(...NAVY);
              doc.text(clean(c.label), cxx + 4, cy, { baseline: "middle" });
              cxx += 4 + doc.getTextWidth(clean(c.label)) + 5;
            });
            by += LIST_LINE;
          });
          by += LIST_LABEL; // skip the vital-label gap to stay aligned
        });
      }
    },
    didDrawPage: (data) => drawFooter(doc, data.pageNumber, doc.internal.pages.length - 1),
  });

  const listFilename = buildReportFilename({
    ...yearMonthFromPeriodDate(periodDate),
    doctype: DOCTYPES.OOR_LIST,
    program: "RPM",
    ext: "pdf",
  });
  if (asBlob) return { blob: doc.output("blob"), filename: listFilename };
  doc.save(listFilename);
};

/* ── Extended Threshold Report PDF (SCRUM-790, Figma 7089-1902) ───── */

// "2026-08-31" -> "08/31/2026" (the contract's PDF date format).
const fmtMdy = (ymd) => {
  if (!ymd || typeof ymd !== "string") return "";
  const [y, m, d] = ymd.split("-");
  return y && m && d ? `${m}/${d}/${y}` : ymd;
};

// Figma px -> mm. The Figma PDF (7089-1902) draws its table 1361px wide; on
// A4 that is the content width, so every Figma measurement scales by FX.
const FX = CONTENT_W / 1361;
const px = (v) => v * FX;
// Figma font px -> pt at the same scale.
const pt = (v) => (v * FX) / 0.3528;

// Systolic BP marker centered at (cx, cy): ▽ hypotension, ▲ stage 2,
// ◆ hypertensive care. `s` = half-width in mm (Figma: 14px icon box).
const drawThresholdIcon = (doc, cx, cy, category, s = px(5.3)) => {
  const meta = ET_CATEGORIES[category];
  if (!meta) return;
  const [r, g, b] = hexToRgb(meta.color);
  const h = s * 0.9; // Figma triangles are 10.7 x 9.5
  if (meta.shape === "diamond") {
    doc.setFillColor(r, g, b);
    doc.triangle(cx - s, cy, cx + s, cy, cx, cy - s, "F");
    doc.triangle(cx - s, cy, cx + s, cy, cx, cy + s, "F");
  } else if (meta.shape === "triangleUp") {
    doc.setFillColor(r, g, b);
    doc.triangle(cx - s, cy + h, cx + s, cy + h, cx, cy - h, "F");
  } else {
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(0.25);
    doc.triangle(cx - s, cy - h, cx + s, cy - h, cx, cy + h, "S");
  }
};

// Header (Figma 7089-2367..2374): logo + navy underline, account line,
// title with the date range on the right, divider. Figma y=40 maps to ET_TOP.
const ET_TOP = 10;
const etY = (figmaY) => ET_TOP + px(figmaY - 40);

const drawEtHeader = (doc, { accountName, reportNumberDisplay, startDate, endDate }) => {
  const logoW = px(277);
  doc.addImage(tocaLogoPng, "PNG", MARGIN, etY(40), logoW, px(37));
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(px(4));
  doc.line(MARGIN, etY(90), MARGIN + logoW, etY(90));

  // Figma shows the provider here; this report is per location, so the
  // account/location name takes that line.
  doc.setFont("DMSans", "bold");
  doc.setFontSize(pt(24));
  doc.setTextColor(...LIGHT);
  doc.text(clean(accountName || ""), MARGIN, etY(126), { baseline: "middle" });
  // The number exists only once submitted; a Draft preview leaves it out.
  if (reportNumberDisplay) {
    doc.setTextColor(...NAVY);
    doc.text(`Report #${reportNumberDisplay}`, PAGE_W - MARGIN, etY(126), {
      baseline: "middle",
      align: "right",
    });
  }

  doc.setFontSize(pt(32));
  doc.setTextColor(...NAVY);
  doc.text("Extended Threshold Report", MARGIN, etY(174), { baseline: "middle" });
  const range = [fmtMdy(startDate), fmtMdy(endDate)].filter(Boolean).join(" – ");
  if (range) {
    doc.setFontSize(pt(24));
    doc.setTextColor(...LIGHT);
    doc.text(range, PAGE_W - MARGIN, etY(174), { baseline: "middle", align: "right" });
  }

  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, etY(205), PAGE_W - MARGIN, etY(205));
  return etY(215);
};

// Keys (Figma 7089-2375): one grey panel holding the Systolic BP and Row
// Colors cards side by side, at the Figma's fixed column widths.
const drawEtKeys = (doc, thresholdKey, highlightKey, top) => {
  const thresholds = resolveThresholdKey(thresholdKey);
  const highlights = resolveHighlightKey(highlightKey);

  // Panel: 1394px wide, 11px wider than the table on each side, with the
  // same padding on all four sides around the cards.
  const PANEL_W = 1394;
  const PANEL_PAD = 10;
  const CARD_H = 83;
  const CARD_GAP = 15;
  const CARD1_W = 461;
  const CARD2_W = PANEL_W - PANEL_PAD * 2 - CARD1_W - CARD_GAP;
  const PANEL_H = CARD_H + PANEL_PAD * 2;
  const panelX = MARGIN - px(11);
  doc.setFillColor(245, 246, 247);
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.2);
  doc.roundedRect(panelX, top, px(PANEL_W), px(PANEL_H), px(10), px(10), "FD");

  const cardY = top + px(PANEL_PAD);
  const card = (x, w, title) => {
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, cardY, w, px(CARD_H), px(10), px(10), "FD");
    doc.setFont("DMSans", "bold");
    doc.setFontSize(pt(16));
    doc.setTextColor(...NAVY);
    doc.text(title, x + px(10), cardY + px(18), { baseline: "middle" });
  };

  // Systolic BP card: 461px, columns 100 / 159 / 144 with 15px gaps.
  const card1X = panelX + px(PANEL_PAD);
  card(card1X, px(CARD1_W), "Systolic BP");
  const colW1 = [100, 159, 144];
  let cx = card1X + px(10);
  thresholds.forEach((k, i) => {
    drawThresholdIcon(doc, cx + px(7), cardY + px(43), k.category);
    doc.setFont("DMSans", "normal");
    doc.setTextColor(...LIGHT);
    doc.setFontSize(pt(14));
    doc.text(clean(k.label), cx + px(18.8), cardY + px(42), { baseline: "middle" });
    doc.setFontSize(pt(12));
    doc.text(clean(k.description), cx + px(18.8), cardY + px(62), { baseline: "middle" });
    cx += px((colW1[i] ?? 144) + 15);
  });

  // Row Colors card fills the rest of the panel; columns 262 / 262 / 270
  // with 15px gaps.
  const card2X = card1X + px(CARD1_W + CARD_GAP);
  card(card2X, px(CARD2_W), "Row Colors");
  const colW2 = [262, 262, 270];
  cx = card2X + px(10);
  highlights.forEach((k, i) => {
    doc.setFillColor(...hexToRgb(ET_HIGHLIGHTS[k.highlight].row));
    doc.setDrawColor(...LIGHT);
    doc.setLineWidth(0.15);
    doc.roundedRect(cx, cardY + px(39), px(31), px(18), px(4), px(4), "FD");
    doc.setFont("DMSans", "normal");
    doc.setFontSize(pt(14));
    doc.setTextColor(...LIGHT);
    doc.text(clean(`= ${k.description}`), cx + px(37), cardY + px(48), {
      baseline: "middle",
    });
    cx += px((colW2[i] ?? 270) + 15);
  });

  // Figma: 30px from the panel to the table header.
  return top + px(PANEL_H + 30);
};

/**
 * Extended Threshold Report PDF. Every header value comes from the report
 * itself: render the FINAL PDF from the post-submit detail so it carries the
 * report number (a Draft has none and leaves it out).
 * `rows` = adaptExtendedThresholdRows(snapshot), in the backend's order.
 */
export const generateExtendedThresholdReportPDF = ({
  accountName,
  reportNumberDisplay,
  startDate,
  endDate,
  thresholdKey,
  highlightKey,
  rows = [],
  asBlob = false,
}) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  registerFonts(doc);
  let startY = drawEtHeader(doc, { accountName, reportNumberDisplay, startDate, endDate });
  startY = drawEtKeys(doc, thresholdKey, highlightKey, startY);

  // Figma columns: Name 211px, DOB 443px, Reason 707px; text inset 24px (the
  // Reason cell also holds the marker, so its text sits at 47px).
  // Rows 46px, header 50px.
  const FONT = pt(16);
  const lineH = FONT * 0.3528 * 1.15;
  const bodyPadY = Math.max((px(46) - lineH) / 2, 0.8);
  const headPadY = Math.max((px(50) - lineH) / 2, 0.8);

  autoTable(doc, {
    head: [["Patient Name", "DOB", "Reason"]],
    body: rows.map((r) => [clean(r.patientName || "—"), r.dob || "—", clean(r.reason)]),
    ...tableTheme(startY, {
      0: { cellWidth: px(211) },
      1: { cellWidth: px(443) },
      2: {
        cellWidth: px(707),
        cellPadding: { top: bodyPadY, bottom: bodyPadY, left: px(47), right: px(8) },
      },
    }),
    styles: {
      font: "DMSans",
      fontStyle: "normal",
      fontSize: FONT,
      cellPadding: { top: bodyPadY, bottom: bodyPadY, left: px(24), right: px(8) },
      textColor: NAVY,
      valign: "middle",
      lineColor: LINE,
      // Figma: horizontal row dividers only.
      lineWidth: { top: 0, right: 0, bottom: 0.2, left: 0 },
    },
    headStyles: {
      font: "DMSans",
      fontStyle: "bold",
      fillColor: NAVY,
      textColor: [255, 255, 255],
      fontSize: FONT,
      valign: "middle",
      halign: "left",
      lineWidth: 0,
      cellPadding: { top: headPadY, bottom: headPadY, left: px(24), right: px(8) },
    },
    // Each row's fill is painted ONCE across the full width (per-cell fills
    // leave hairline seams between columns in PDF viewers).
    didParseCell: (data) => {
      data.cell.styles.fillColor = false;
    },
    willDrawCell: (data) => {
      if (data.column.index !== 0) return;
      let fill = NAVY;
      if (data.section === "body") {
        // The highlight colors the WHOLE row; null = first appearance, no fill.
        const highlight = rows[data.row.index]?.highlight;
        fill = ET_HIGHLIGHTS[highlight] ? hexToRgb(ET_HIGHLIGHTS[highlight].row) : [255, 255, 255];
      }
      doc.setFillColor(...fill);
      doc.rect(data.cell.x, data.cell.y, px(1361), data.cell.height, "F");
    },
    didDrawCell: (data) => {
      if (data.section !== "body" || data.column.index !== 2) return;
      const row = rows[data.row.index];
      if (!row) return;
      drawThresholdIcon(doc, data.cell.x + px(31), data.cell.y + data.cell.height / 2, row.category);
    },
  });
  // Footers go on after the table so "Page X of Y" knows the final count.
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p += 1) {
    doc.setPage(p);
    drawFooter(doc, p, totalPages);
  }

  const filename = buildReportFilename({
    ...yearMonthFromPeriodDate(endDate),
    doctype: DOCTYPES.OOR_EXTENDED_THRESHOLD,
    program: "RPM",
    ext: "pdf",
  });
  if (asBlob) return { blob: doc.output("blob"), filename };
  doc.save(filename);
};

/* ── Excel (.xlsx) of the readings ─────────────────────────────────── */

const UNIT_BY_TYPE = {
  blood_pressure: "mmHg",
  heart_rate: "bpm",
  weight: "lbs",
  temperature: "°F",
  blood_glucose: "mg/dL",
  pulse_ox: "%",
};

/**
 * Excel of "just the readings" (p.joshi). Consumes the flattened list-report
 * rows from `flattenListReadings(apiData)` — one row per reading, already
 * carrying patient / provider / value / categories from the backend.
 */
export const generateReadingsExcel = ({ rows = [], accountName, periodDate, asBlob = false }) => {
  const data = rows.map((r) => ({
    "Patient Name": r.patientName || "",
    DOB: r.dob || "",
    Provider: r.providerName || "",
    "Vital Type": VITALS_BY_KEY[r.readingTypeKey]?.label || r.readingType || "",
    "Reading Value": r.value == null ? "" : String(r.value),
    Unit: UNIT_BY_TYPE[r.readingTypeKey] || "",
    "Date/Time": formatDateTime(r.recordedAt),
    "Category(ies)": r.categories || "",
    "Out of Range": r.outOfRange || "",
  }));

  const ws = XLSX.utils.json_to_sheet(
    data.length
      ? data
      : [
          {
            "Patient Name": "",
            DOB: "",
            Provider: "",
            "Vital Type": "",
            "Reading Value": "",
            Unit: "",
            "Date/Time": "",
            "Category(ies)": "",
            "Out of Range": "",
          },
        ]
  );
  ws["!cols"] = [
    { wch: 22 }, { wch: 12 }, { wch: 18 }, { wch: 16 }, { wch: 14 },
    { wch: 8 }, { wch: 20 }, { wch: 24 }, { wch: 12 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "OOR Readings");
  const excelFilename = buildReportFilename({
    ...yearMonthFromPeriodDate(periodDate),
    doctype: DOCTYPES.OOR_LIST,
    program: "RPM",
    ext: "xlsx",
  });
  if (asBlob) {
    const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    return {
      blob: new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      filename: excelFilename,
    };
  }
  XLSX.writeFile(wb, excelFilename);
};
